// 3rd-party modules
import moment from 'moment-timezone';
import { message, Slider, Tooltip } from 'antd';
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";

// project modules
import AccountDeviceActivityLogs from '../device-activity-logs/AccountDeviceActivityLogs';
import AccountDeviceBandwidthLogs from '../device-bandwidth-logs/AccountDeviceBandwidthLogs';
import AccountBlockedSites from '../blocked-sites/AccountBlockedSites';
import AccountDeviceBlockingModal from './AccountDeviceBlockingModal';
import AccountDeviceGroupCreationModal from '../device-groups/AccountDeviceGroupCreationModal';
import AccountDeviceGroupMenuModal from '../device-groups/AccountDeviceGroupMenuModal';
import AccountDeviceGroupModal from '../device-groups/AccountDeviceGroupModal';
import AccountDeviceMalwareLogs from '../device-malware-logs/AccountDeviceMalwareLogs';
import AccountDeviceExplicitLogs from '../device-explicit-logs/AccountDeviceExplicitLogs';
import AccountInternetSchedules from '../internet-schedules/AccountInternetSchedules';
import Button from "../../shared/button";
import ConfirmationPopup from '../../shared/popup/confirmationPopup';
import Input from "../../shared/inputs/input";
import Loader from "../../shared/loader";
import Popup from '../../shared/popup/popup';
import Select from '../../shared/inputs/select';
import TabStrip from '../../shared/tabstrip';
import TimerComponent from '../../shared/timer';
import Tab from '../../shared/tabstrip/tab';
import yup from "../../../plugins/yup";
import { apiCall } from "../../../helpers/apiHelper";
import { CONSTANTS } from '../../../helpers/defines';
import { convertToSnakecase } from '../../../helpers/objectHelper';
import { getDeviceSignalRateClassIcon } from '../../../helpers';

// apis
import * as AccountDeviceApi from '../../../apis/accountDeviceApi';
import * as CommonValueApi from '../../../apis/commonValueApi';
import * as DashApi from '../../../apis/dashApi';

// models
import { Account } from '../../../models/account';
import { AccountDevice } from '../../../models/accountDevice';
import { AccountKiboshDevice } from '../../../models/accountKiboshDevice';
import { ApiResponse } from "../../../models/response";
import { accountDeviceBlockViewModel, accountDeviceInsertViewModel } from '../../../models/types/accountDevice';
import { AccountDeviceGroup } from '../../../models/accountDeviceGroup';
import { CommonValue } from '../../../models/commonValue';

type Props = {
  account: Account;
  accountDevice: AccountDevice;
  accountKiboshDevice: AccountKiboshDevice;
  closeOnSave?: boolean;
  modalTitle?: string;
  preSelectedTab?: string;
  showGroupDetails?: boolean;
  showTabs?: boolean;
  open: boolean;
  onClose?: () => void;
  onSave?: (accountDevice?: any) => void;
  onGroupSave?: () => void;
};

export default function AccountDeviceModal({ account, accountDevice, accountKiboshDevice, preSelectedTab, closeOnSave = false, modalTitle = "", open, showGroupDetails = true, showTabs = true, onClose, onSave, onGroupSave }: Props) {
  const schema = yup.object().shape({
    name: yup.string().label("Device Name").max(256).required(),
    staticIp: yup.string().label("Device Static IP").max(16).nullable()
  });
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [isClearStaticIPConfirmationPopupOpen, setIsClearStaticIPConfirmationPopupOpen] = useState(false);
  const [internetData, setInternetData] = useState<accountDeviceBlockViewModel | null>(null);
  const [isHasInternetConfirmationPopupOpen, setHasInternetConfirmationPopupOpen] = useState(false);
  const [hasInternet, setHasInternet] = useState<boolean>(false);
  const [showAccountDeviceGroupModal, setShowAccountDeviceGroupModal] = useState(false);
  const [showAccountDeviceGroupMenuModal, setShowAccountDeviceGroupMenuModal] = useState(false);
  const [showAccountDeviceGroupCreationModal, setShowAccountDeviceGroupCreationModal] = useState(false);
  const [currentAccountDeviceGroup, setCurrentAccountDeviceGroup] = useState<AccountDeviceGroup | null>(null);
  const [currentAccountDevice, setCurrentAccountDevice] = useState<AccountDevice>(accountDevice);
  const [deviceGroupName, setDeviceGroupName] = useState<string>("");
  const [deviceTypes, setDeviceTypes] = useState<CommonValue[]>([]);
  const [loadingDevicesTypeList, setLoadingDevicesTypeList] = useState(false);
  const [showStaticIpActivationText, setShowStaticIpActivationText] = useState(false);

  const [accountDeviceGroupLoading, setAccountDeviceGroupLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [minutesBeforeExpiration, setMinutesBeforeExpiration] = useState<number>(0);
  const [hoursBeforeExpiration, setHoursBeforeExpiration] = useState<number>(0);
  const [internetPauseRemainingTime, setInternetPauseRemainingTime] = useState<number>(0);
  const [internetPauseTotalTime, setInternetPauseTotalTime] = useState<number>(0);
  const [locationLink, setLocationLink] = useState<string>("about:blank")


  const [vpnGpsData, setVpnGpsData] = useState<any[] | null>(null)

  const { control, handleSubmit, setValue } = useForm<accountDeviceInsertViewModel | any>({
    defaultValues: { ...currentAccountDevice },
    resolver: yupResolver(schema),
  });

  const abortController = new AbortController();

  useEffect(() => {
    setShowStaticIpActivationText( currentAccountDevice?.staticIp != null && currentAccountDevice?.staticIp.toString() != "" )
    getDeviceTypesAsync();
  }, []);

  useEffect(() => {
    if (open) {
      if (preSelectedTab) {
        switch (preSelectedTab) {
          case 'details':
            setCurrentTab(0)
            break;
          case 'blocked-sites':
            setCurrentTab(1)
            break;
          case 'internet-schedules':
            setCurrentTab(2)
            break;
          case 'activity-logs':
            setCurrentTab(3)
            break;
          case 'bandwidth-logs':
            setCurrentTab(4)
            break;
          case 'internet-pause':
            setCurrentTab(5)
            break;
          case 'malware-logs':
            setCurrentTab(6)
            break;
          case 'explicit-logs':
            setCurrentTab(7)
            break;
        }
      }
    }
  }, [open]);

  useEffect(() => {
    setHasInternet(!currentAccountDevice.isBlocked);
    if (currentAccountDevice.groupId) {
      getAccountDeviceGroupAsync();
    }
    getVpnGpsDataAsync();


    if (currentAccountDevice.isBlocked) {
      const remainingTime = getTimeRemaining();
      setInternetPauseRemainingTime(Math.floor(remainingTime.total));
      const totalTime = getTimeTotal();
      setInternetPauseTotalTime(Math.floor(totalTime.total));
    }
  }, [currentAccountDevice]);

  useEffect(() => {
    setDeviceGroupName(currentAccountDeviceGroup?.name || "")
  }, [currentAccountDeviceGroup]);

  const getDeviceTypesAsync = async () => {
    const data = await getDeviceTypes(abortController.signal);

    setDeviceTypes(data.filter(x => x.value !== "dns"));
  }

  const getDeviceTypes = async (abortSignal?: AbortSignal) => {
    setLoadingDevicesTypeList(true);
    const response = await apiCall(CommonValueApi.getClientDeviceTypes(abortSignal));
    setLoadingDevicesTypeList(false);

    return response.success ? CommonValue.toArrayOfClass(response.data?.value || []) : [];
  }





  const onCancel = () => {
    abortController.abort();

    if (onClose) onClose();
  };

  const onSubmit: SubmitHandler<accountDeviceInsertViewModel> = async (formData: accountDeviceInsertViewModel) => {
    let response: ApiResponse;

    setLoading(true);

    formData.kiboshDeviceReference = accountKiboshDevice.kiboshDeviceReference!;
    const data = convertToSnakecase(formData, [], ["kiboshDeviceReference"]);



    if (!currentAccountDevice?.id)
      response = await apiCall(AccountDeviceApi.insertAccountDevice(formData, abortController.signal));
    else
      response = await apiCall(DashApi.updateClientDevice(currentAccountDevice.id, data, abortController.signal));

    if (response.success) {
      setShowStaticIpActivationText( data["static_ip"] != null && data["static_ip"].toString() != "" )
      message.success(`Device ${!currentAccountDevice?.id ? 'added' : 'edited'} successfully.`);

      

      if (onSave) onSave(response.data?.value);

      if (closeOnSave) {
        open = false;

        onCancel();
      }

    } else
      message.error(response.error?.value);

    setLoading(false);
  };

  const onClearStaticIpClick = () => {
    setIsClearStaticIPConfirmationPopupOpen(true)
  }

  const handleClearStaticIp = async () => {
    setLoading(true);

    let response: ApiResponse = await apiCall(DashApi.deleteClientDeviceStaticIp(accountKiboshDevice.kiboshDeviceReference!, currentAccountDevice.id!, abortController.signal));

    if (response.success) {
      message.success(`Device Static IP deleted successfully.`);
      setValue("staticIp", "");
      setIsClearStaticIPConfirmationPopupOpen(false);

      if (onSave) onSave(response.data?.value);
        setShowStaticIpActivationText( false )

    } else
      message.error(response.error?.value);

    setLoading(false);
  }

  const getAccountDevice = async () => {
    if (!accountKiboshDevice.kiboshDeviceReference || !currentAccountDevice.id) {
      return;
    }

    setLoading(true);
    const response = await apiCall(DashApi.getClientDeviceById(accountKiboshDevice.kiboshDeviceReference!, currentAccountDevice.id!, abortController.signal));
    setLoading(false);

    if (response.success) {
      // eslint-disable-next-line eqeqeq
      const data = response.data?.value?.length ? response.data?.value?.filter((x: any) => x.id == currentAccountDevice.id) : response.data?.value;

      return AccountDevice.toClass(data.length ? data[0] : data);
    }
    return currentAccountDevice;

  };

  const onAccountDeviceGroupSave = async (e?: AccountDeviceGroup) => {
    getAccountDeviceGroupAsync();
    getVpnGpsDataAsync();
    setCurrentAccountDevice(await getAccountDevice() || currentAccountDevice);
    if (onSave) {
      onSave();
    }
    if (onGroupSave) {
      onGroupSave();
    }
  }

  
  const onAccountDeviceBlockingSave = async () => {

    setHasInternet(!hasInternet)

    if (onSave) {
      let saveRet:any = onSave();
      if( saveRet instanceof Promise) {
        await saveRet;
      }
    }


    const newDev = await getAccountDevice() 
    //alert(JSON.stringify(newDev))
    const dev = newDev || currentAccountDevice
    setHasInternet(!dev.isBlocked);
    setCurrentAccountDevice(dev);

  }

  const onEditGroupClick = () => {
    setShowAccountDeviceGroupMenuModal(true);
  };

  const onCreateGroupClick = () => {
    setShowAccountDeviceGroupCreationModal(true);
  };



  const onMalwareCountClick = (accountDevice: AccountDevice) => {
    if( !!(accountDevice.detectedMalware)  && accountDevice.detectedMalware?.length > 0) {
      setCurrentTab(6)    
    }
  };
  const onExplicitCountClick = (accountDevice: AccountDevice) => {
    if( !!(accountDevice.detectedExplicit?.length) && accountDevice.detectedExplicit?.length > 0) {
      setCurrentTab(7)
    }
  };





  const getAccountDeviceGroupAsync = async () => {
    setCurrentAccountDeviceGroup(await getAccountDeviceGroup() || null);
  }

  const getAccountDeviceGroup = async () => {
    if (!accountKiboshDevice.kiboshDeviceReference || !currentAccountDevice.groupId) {
      return;
    }

    setAccountDeviceGroupLoading(true);
    const response = await apiCall(DashApi.getDeviceGroupById(accountKiboshDevice.kiboshDeviceReference!, currentAccountDevice.groupId!, true, abortController.signal));
    setAccountDeviceGroupLoading(false);

    return response.success ? AccountDeviceGroup.toClass(response.data?.value) : null;
  };

  const getVpnGpsDataAsync = async () => {
    let vgd = await getVpnGpsData() || null
    setVpnGpsData(vgd);
    if(vgd != null && vgd[0] != null) {
      let gps = vgd[0]
      setLocationLink("https://maps.google.com/maps?q=" + gps.latitude + "," + gps.longitude + "&t=&z=14&ie=UTF8&iwloc=&output=embed")
    }
  }

  const getVpnGpsData = async (abortSignal?: AbortSignal) => {
    if (!accountKiboshDevice.kiboshDeviceReference || !currentAccountDevice.isVpn) {
      return;
    }

    let today = moment().format("YYYY-MM-DD");
    let response = await apiCall(DashApi.getVpnMonitorEvents(accountKiboshDevice.kiboshDeviceReference!, currentAccountDevice.id!, today, abortController.signal))

    if(response.success && response.data?.value != null) {
      
      let validGps = response.data?.value.filter( (x:any) => x.latitude != null && x.longitude != null  )
      let finalGps = [ ]


      if(validGps.length > 0) {
        finalGps.push( validGps.shift() )
        let gpsIntervalData:any = {}
        while(validGps.length > 0) { 
          let nextGps = validGps.shift();
          let m = moment(nextGps.event_time)
          let interval = m.add( -1 *( m.minutes() % 15), "minutes")
          gpsIntervalData[ interval.format("YYYY-MM-DD-HH-mm") ] = nextGps
        }
        Object.keys(gpsIntervalData).sort().reverse().forEach( key => { 
          finalGps.push( gpsIntervalData[key] )
        });



       //finalGps = finalGps.concat(  validGps.filter( (x:any) => (  moment(x.event_time).minutes() % 15 == 0  ) ))
      }
      return finalGps;

    } else {
      return null;
    }
       
 
    //return response.success ? response.data?.value.filter( (x:any) => x.latitude != null && x.longitude != null  ) : null;

  }






  const onInternetPauseClick = async () => {
    const data: accountDeviceBlockViewModel = {
      blocked: hasInternet,
      deviceId: `${currentAccountDevice.id!}`,
      kiboshDeviceReference: accountKiboshDevice.kiboshDeviceReference!,
      // minutesBeforeExpiration:  0
      minutesBeforeExpiration: hasInternet ? (hoursBeforeExpiration * 60) + minutesBeforeExpiration : 0
    }
    setInternetData(data);
    setHasInternetConfirmationPopupOpen(true);
  }

  // Format time remaining to MM:SS format
  const formatedTime = (): string => {
    return `${hoursBeforeExpiration}:${minutesBeforeExpiration < 10 ? '0' : ''}${minutesBeforeExpiration}`;
  };

  const getTimeRemaining = () => {
    const now = moment(); //todays date
    const end = moment(currentAccountDevice.blockedUntil); // another date
    const duration = moment.duration(end.diff(now));
    const total = duration.asSeconds();
    const seconds = Math.floor(
      total % 60
    );
    const minutes = Math.floor(
      (total / 60) % 60
    );
    const hours = Math.floor(
      (total / 60 / 60) % 24
    );
    return {
      total,
      hours,
      minutes,
      seconds,
    };
  };

  const getTimeTotal = () => {
    const start = moment(currentAccountDevice.blockedAt); //begining date
    const end = moment(currentAccountDevice.blockedUntil); // another date
    const duration = moment.duration(end.diff(start));
    const total = duration.asSeconds();
    const seconds = Math.floor(
      total % 60
    );
    const minutes = Math.floor(
      (total / 60) % 60
    );
    const hours = Math.floor(
      (total / 60 / 60) % 24
    );
    return {
      total,
      hours,
      minutes,
      seconds,
    };
  };


  const getLastSeen = (data: any): string =>   {
    let lastSeen = data.updatedAt ? moment(data.updatedAt).fromNow() : '-'
    if( data.connectionStatus != "offline" ) {
      if( lastSeen.match(/seconds/) || lastSeen.match(/minute ago/) ) {
        lastSeen = "now"
      }
      if( lastSeen.match(/minutes ago/) ) {
        let minutes = parseInt(lastSeen)
        if( minutes < 20 ) {
          lastSeen = "now"
        }
      }
    }
    return lastSeen;
  }

  const getDetailsLatencyText = () => {
    let pingMs = currentAccountDevice?.pingMs!
    pingMs = pingMs == null ? -1 : pingMs

    if(pingMs <0 ) { 
      if( currentAccountDevice.connectionStatus == "remote" ) {
        return "Remote"
      } else {
        return "Offline"
      }
    } else {
      return `Latency: ${pingMs.toFixed(2)} ms`
    }
  };
  const getDetailsLatencyIcon = () => {
    let pingMs = currentAccountDevice?.pingMs!
    pingMs = pingMs == null ? -1 : pingMs
    if(pingMs <0 && currentAccountDevice?.connectionStatus == "remote" ) {
        return getDeviceSignalRateClassIcon(5.0)
    } else {
        return getDeviceSignalRateClassIcon(pingMs)
    }
  };

  
  const getStatus = (record:any ):string => {
    let lastSeen = record.updatedAt
    let displayStatus = lastSeen.match(/now$/) || record.connectionStatus == "remote" ? record.connectionStatus : record.connectionStatus + " (since " + record.updatedAt.replace(/^.*@/, "") + ")"
    return displayStatus
  } 



  const renderDetailsTab = () => {

    let haveVpnGpsData = vpnGpsData != null && vpnGpsData.length > 0
    let displayGpsData: any[] = []
    if(haveVpnGpsData) {
      displayGpsData = vpnGpsData ? vpnGpsData.map((gps,index) => ( 
        <div data-color="main" style={{ textDecoration: "underline" }} >
          <a href="javascript:;" onClick={ () => { setLocationLink("https://maps.google.com/maps?q=" + gps.latitude + "," + gps.longitude + "&t=&z=14&ie=UTF8&iwloc=&output=embed") } } >
            { moment(gps.event_time).format(CONSTANTS.DEFAULT_TIME_FORMAT) }
          </a>
          <br/>
        </div>  
      )) : []
    } 
    return (
      <group style={{ overflow:"scroll" }}>
        {accountDeviceGroupLoading && <Loader />}
        <group data-direction="column" width='auto' data-border="">
          <group data-space="15" data-gap="10" data-direction="column">
            <Input
              control={control}
              label="Device Name"
              dataLength="auto"
              size="large"
              name="name"
            />
            <Select
              control={control}
              label="Device Type"
              dataLength="auto"
              name="deviceType"
              allowSearch={true}
              loading={loadingDevicesTypeList}
              options={
                deviceTypes?.map((item) => {
                  return {
                    text: item.valueCaption!,
                    value: item.value!,
                  };
                }) || []
              }
              />
            <Tooltip title="Reboot Device, Bounce WiFi or wait several hours to activate static IP">
              <Input
                control={control}
                label="Device Static IP"
                dataLength="auto"
                size="large"
                name="staticIp"
                button={
                  <Button primary material onClick={onClearStaticIpClick}>
                    <text>Clear Static IP</text>
                  </Button>
                }
              />
            </Tooltip>
          </group>
          <group
            data-space="10"
            data-align="center"
            data-gap="10"
            data-border="top"
          >
            <Button material primary onClick={handleSubmit(onSubmit)}>
              <text>Save Changes</text>
            </Button>
          </group>
          {
            showGroupDetails &&
            <>
              <group
                data-space="10"
                data-align="center"
                data-gap="10"
                data-border="top"
              >
                <Input
                  label="Profile Membership"
                  dataLength="auto"
                  size="large"
                  value={deviceGroupName}
                  disabled
                  button={
                    <Button primary material disabled={!currentAccountDeviceGroup ? true : undefined} onClick={onEditGroupClick}>
                      <text>Edit Profile</text>
                    </Button>
                  }
                />
              </group>
              <group
                data-space="10"
                data-align="center"
                data-gap="10"
              >
                <Button material primary icon="add" disabled={currentAccountDeviceGroup ? true : undefined} onClick={onCreateGroupClick}>
                  <text>Create a new Profile</text>
                </Button>
              </group>
            </>
          }
          <group
            data-space="10"
            data-align="center"
            data-gap="10"
          >
            <text><text data-weight="700">Last Seen:</text> { currentAccountDevice?.updatedAt?.replace(/^.*@/, "") }</text>
          </group>


          <group
            data-space="10"
            data-align="center"
            data-gap="10"
          >
            <text data-weight="700">Connection:</text>
            <text data-color={ currentAccountDevice?.connectionStatus == "offline" ? "red" : "black" } data-weight={currentAccountDevice?.connectionStatus == "offline" ? "900" : "300"} >{ getStatus(currentAccountDevice) }</text>
          </group>

          { currentAccountDevice?.isVpn &&
            <group
              data-space="10"
              data-align="center"
              data-gap="10"
            >
              <text data-weight="700">VPN:</text>
              <text data-color={ currentAccountDevice?.vpnIsConnected ? "black" : "red" } data-weight={ currentAccountDevice?.vpnIsConnected ? "300" : "900"} >{!!currentAccountDevice?.vpnIsConnected ? "On" : "Off"}</text>
            </group>

          }

          <group
            data-space="10"
            data-align="center"
            data-gap="10"
            onClick={ () => onMalwareCountClick(currentAccountDevice) }
          >
            <text data-weight="700">Malware:</text>
            <text data-color={ currentAccountDevice?.detectedMalware?.length! > 0 ? "red" : "black" } data-weight={currentAccountDevice?.detectedMalware?.length! > 0  ? "900" : "300"} >{ currentAccountDevice?.detectedMalware?.length! }</text>
          </group>

          <group
            data-space="10"
            data-align="center"
            data-gap="10"
            onClick={ () => onExplicitCountClick(currentAccountDevice) }
          >
            <text data-weight="700">Explicit:</text>
            <text data-color={ currentAccountDevice?.detectedExplicit?.length! > 0 ? "red" : "black" } data-weight={ currentAccountDevice?.detectedExplicit?.length! > 0  ? "900" : "300"} >{ currentAccountDevice?.detectedExplicit?.length! }</text>
          </group>



        </group>
        <group data-width="auto">
          <group data-space="15" data-gap="20" data-direction="column">
            <group data-direction="column">
              <group data-justify='center'>
                <icon large='' data-color='main'>{ getDetailsLatencyIcon() }</icon>
              </group>
              <group data-justify='center'>
		  <text>{  getDetailsLatencyText() }</text>
              </group>
              { haveVpnGpsData &&
                <>
                  <group data-justify='center'>
                    <div data-gap="20" data-space="15" > </div>
                  </group>

                  <group data-justify='center'>
                    <div data-gap="20" data-space="15" >
                      <text data-weight="700" >GPS Location History (for today)</text>
                    </div>
                  </group>
                  <group data-justify='center' style={{ display: "block" }}>
                    <div style={{ display:"block", width:"550px" }} >
                      <span style={{ float: "left", clear:"left", width:"80px" }} >
                        { displayGpsData }
                      </span>
 	              <span style={{ width:"400px", display:"inline" }} >
                        <iframe style={{ display:"block", width:"400px", height:"400px"}} src={ locationLink }></iframe>
                      </span>
                    </div>
                  </group>
                </>
              }
              
            </group>
          </group>
        </group>
      </group>
    );
  }

  const renderInternetPauseTab = () => {
    return (
      <>
        <view data-gap="20" data-space="20" data-max-length="400" data-position="center" data-border="none">
          <group data-width="auto" data-space="15" data-justify="center">
            <Tooltip title="Internet Pause" arrow={false}>
              <group data-width="auto" data-color="white">
                <group
                  data-background="main-background"
                  data-length="200"
                  data-height="200"
                  data-elevation="6"
                  rounded
                  data-radius="full"
                  data-border='outline'
                  data-space="5"
                  onClick={onInternetPauseClick}
                  data-text-size="xx-large"
                  data-weight="600"
                  data-color="gray"
                >
                  {hasInternet ?
                  <text data-position="center" className="timer-text" data-color="gray">
                    {formatedTime()}
                  </text>
                  : <TimerComponent remaining={internetPauseRemainingTime} duration={internetPauseTotalTime} />}
                </group>
              </group>
            </Tooltip>
          </group>
          {!hasInternet ?
            <></>
            :
            <group data-space-horizontal="30" data-justify="center" data-direction="column" data-gap="10">
              <text>Minutes:</text>
              <Slider data-width="auto" step={5} min={0} max={59} onChange={setMinutesBeforeExpiration} />
              <text>Hours:</text>
              <Slider min={0} max={23} onChange={setHoursBeforeExpiration} />
            </group>
          }
          <group data-space-horizontal="30" data-justify="center" data-direction="column" data-gap="10">
            <Button
              material
              text={hasInternet ? "Pause" : "Unpause"}
              primary
              onClick={onInternetPauseClick}
            />
          </group>

        </view>
      </>
    );
  }

  const onStepSave = (acc: Account, step: string) => {
    switch (step) {
      default:
        break;
    }
  };



  const getDeviceName = (deviceItem:any) => {
    let isVpn = ( deviceItem["is_vpn"] == true )
    let dt  =  deviceItem.name!
    dt = dt == null ? "" + deviceItem.id! : dt
    dt = isVpn ? dt  + " (VPN)" : dt
    dt = (!isVpn) && deviceItem.ip ? dt + " (" + deviceItem.ip.replace("disconnected", "offline") + ")" : dt
    return dt
  };


  const getDefaultTitle = (kd:any, dev:any) => {

    /*
          case 'details':
            setCurrentTab(0)
            break;
          case 'blocked-sites':
            setCurrentTab(1)
            break;
          case 'internet-schedules':
            setCurrentTab(2)
            break;
          case 'activity-logs':
            setCurrentTab(3)
            break;
          case 'bandwidth-logs':
            setCurrentTab(4)
            break;
          case 'internet-pause':
            setCurrentTab(5)
            break;
          case 'malware-logs':
            setCurrentTab(6)
            break;

    */
    let tabNames:any = {
      0 : "Details",
      1 : "Filters",
      2 : "Schedules",
      3 : "Activity Logs",
      4 : "Bandwidth Logs",
      5 : "Pause",
      6 : "Malware Logs",
      7 : "Explicit Logs"
    }; 



    if(dev == null || !dev.id)
    { 
      return kd.kiboshDeviceName + " - New Device"
    } else {
      let tabText = tabNames[currentTab] ? " > " + tabNames[currentTab] : ""
      return getDeviceName(dev) + tabText;
    }
  };



  return (
    <Popup
      title={modalTitle || getDefaultTitle(accountKiboshDevice, currentAccountDevice) }
      onCancel={onCancel}
      onClose={onCancel}
      noCommandbar={true}
      adaptive
      fixSize="larger"
    >
      {loading &&
        <Loader />
      }
      <TabStrip
        skipSecondaryTabs={false}
        selectedIndex={currentTab}
        showTabs={showTabs}
        onChange={setCurrentTab}
        id="device-tab">
        <Tab title='Details'>
          {renderDetailsTab()}
        </Tab>
        <Tab title='Filters' disabled={!account.accountId || !accountKiboshDevice.kiboshDeviceReference || !currentAccountDevice.id || ( !!currentAccountDevice.groupId && (!currentAccountDevice.isVpn))  }>
          {!!account.accountId && !!accountKiboshDevice.kiboshDeviceReference && !!currentAccountDevice.id && ( !currentAccountDevice.groupId || !!currentAccountDevice.isVpn) &&
            <AccountBlockedSites refresh={!!account.accountId} account={account} kiboshDeviceReference={accountKiboshDevice.kiboshDeviceReference} deviceReference={currentAccountDevice.id} onCancel={onCancel} onSave={(acc) => onStepSave(acc, 'blockedSites')} />
          }
        </Tab>
        <Tab title='Scheduler' disabled={!account.accountId || !accountKiboshDevice.kiboshDeviceReference || !currentAccountDevice.id || !!currentAccountDevice.groupId}>
          {!!account.accountId && !!accountKiboshDevice.kiboshDeviceReference && !!currentAccountDevice.id && !currentAccountDevice.groupId &&
            <AccountInternetSchedules refresh={!!account.accountId} account={account} kiboshDeviceReference={accountKiboshDevice.kiboshDeviceReference} deviceReference={currentAccountDevice.id} onCancel={onCancel} onSave={(acc) => onStepSave(acc, 'internetSchedules')} />
          }
        </Tab>
        <Tab title='Activity Logs' disabled={!account.accountId || !accountKiboshDevice.kiboshDeviceReference || !currentAccountDevice.id}>
          {!!account.accountId && !!accountKiboshDevice.kiboshDeviceReference && !!currentAccountDevice.id &&
            <AccountDeviceActivityLogs refresh={!!account.accountId} account={account} kiboshDeviceReference={accountKiboshDevice.kiboshDeviceReference} deviceReference={currentAccountDevice.id} hasUpperLevelControl={!!currentAccountDeviceGroup} onCancel={onCancel} onSave={(acc) => onStepSave(acc, 'activityLogs')} />
          }
        </Tab>
        <Tab title='Bandwidth Logs' disabled={!account.accountId || !accountKiboshDevice.kiboshDeviceReference || !currentAccountDevice.id}>
          {!!account.accountId && !!accountKiboshDevice.kiboshDeviceReference && !!currentAccountDevice.id &&
            <AccountDeviceBandwidthLogs refresh={!!account.accountId} account={account} kiboshDeviceReference={accountKiboshDevice.kiboshDeviceReference} deviceReference={currentAccountDevice.id} onCancel={onCancel} onSave={(acc) => onStepSave(acc, 'bandwidthLogs')} />
          }
        </Tab>
        <Tab title='Pause' disabled={!account.accountId || !accountKiboshDevice.kiboshDeviceReference || !currentAccountDevice.id}>
          {renderInternetPauseTab()}
        </Tab>
        <Tab title='Malware Logs' disabled={!account.accountId || !accountKiboshDevice.kiboshDeviceReference || !currentAccountDevice.id || !currentAccountDevice.detectedMalware?.length}>
          {!!account.accountId && !!accountKiboshDevice.kiboshDeviceReference && !!currentAccountDevice.id &&
            <AccountDeviceMalwareLogs refresh={!!account.accountId} account={account} kiboshDeviceReference={accountKiboshDevice.kiboshDeviceReference} deviceReference={currentAccountDevice.id} onCancel={onCancel} onSave={(acc) => onStepSave(acc, 'malwareLogs')} />
          }
        </Tab>
        <Tab title='Explicit Logs' disabled={!account.accountId || !accountKiboshDevice.kiboshDeviceReference || !currentAccountDevice.id || !currentAccountDevice.detectedExplicit?.length}>
          {!!account.accountId && !!accountKiboshDevice.kiboshDeviceReference && !!currentAccountDevice.id &&
            <AccountDeviceExplicitLogs refresh={!!account.accountId} account={account} kiboshDeviceReference={accountKiboshDevice.kiboshDeviceReference} deviceReference={currentAccountDevice.id} onCancel={onCancel} onSave={(acc) => onStepSave(acc, 'explicitLogs')} />
          }
        </Tab>

      </TabStrip>
      {isClearStaticIPConfirmationPopupOpen && (
        <ConfirmationPopup
          showButton={false}
          positiveButtonText="Clear Static IP"
          positiveCallback={handleClearStaticIp}
          negativeCallback={() => {
            setIsClearStaticIPConfirmationPopupOpen(false);
          }}
        />
      )}
      {!!showAccountDeviceGroupCreationModal && (
        <AccountDeviceGroupCreationModal
          open={showAccountDeviceGroupCreationModal}
          closeOnSave={true}
          accountDevice={currentAccountDevice!}
          kiboshDeviceReference={accountKiboshDevice.kiboshDeviceReference!}
          onClose={() => setShowAccountDeviceGroupCreationModal(false)}
          onSave={(e) => onAccountDeviceGroupSave(e)}
        />
      )}
      {!!showAccountDeviceGroupModal && (
        <AccountDeviceGroupModal
          open={showAccountDeviceGroupModal}
          account={account!}
          accountDeviceGroup={currentAccountDeviceGroup!}
          accountKiboshDevice={accountKiboshDevice}
          preSelectedTab=""
          onClose={() => setShowAccountDeviceGroupModal(false)}
          onSave={(e) => onAccountDeviceGroupSave(e)}
        />
      )}
      {!!showAccountDeviceGroupMenuModal && (
        <AccountDeviceGroupMenuModal
          open={showAccountDeviceGroupMenuModal}
          account={account!}
          accountDeviceGroup={currentAccountDeviceGroup!}
          accountDeviceGroups={[currentAccountDeviceGroup!]}
          accountKiboshDevice={accountKiboshDevice!}
          onClose={() => setShowAccountDeviceGroupMenuModal(false)}
          onSave={onAccountDeviceGroupSave}
        />
      )}
      {!!isHasInternetConfirmationPopupOpen && (
        <AccountDeviceBlockingModal
          open={isHasInternetConfirmationPopupOpen}
          accountDeviceId={currentAccountDevice.id!}
          accountDeviceName={currentAccountDevice.name!}
          blockingData={internetData!}
          closeOnSave={true}
          onClose={() => setHasInternetConfirmationPopupOpen(false)}
          onSave={onAccountDeviceBlockingSave}
        />
      )}
    </Popup>
  );
}
